import { action, observable } from "mobx";
import { PasswordSimilarityRequestModel } from "@api-types/partner-registration/Index";
import { LugasErrorCodes } from "@data-types/user/LugasErrorCodes";
import { registrationValidationSingle } from "@utils/membership/registrationValidation";
import { MainRegistrationStore } from "../MainRegistrationStore";
import {
	validateBooleanResponse,
	validatePasswordSimilarityResponse,
} from "@utils/lookups/validators";
import { PartnerLookupService } from "@services/membership/partner-registration";
import {
	PlatformPartnerRegistrationApiService,
	PlatformRegistrationApiService,
} from "@api-services/platform";
import { RegistrationFormData } from "@data-types/membership/registration";
import type { RootStore } from "@state/stores/types";
import { analyticsTracker } from "@utils/analytics";
import { RegistrationTypeEnum } from "@data-types/analytics-tracker/RegistrationTypeEnum";

export default class PartnerRegistrationStore extends MainRegistrationStore {
	@observable takeoverTempInfoId: string | null = null;
	rootStore: RootStore;
	constructor(rootStore: RootStore) {
		super();
		this.rootStore = rootStore;
	}

	@action.bound
	async onInitialize() {
		if (this.rootStore.isIpRestricted) {
			return;
		}

		analyticsTracker.trackRegistrationStarted(
			RegistrationTypeEnum.PartnerRegistration
		);

		this.loader.suspend();

		try {
			this.titleData = await PartnerLookupService.getTitles();
			this.countryData = await PartnerLookupService.getCountries();
			this.languageData = await PartnerLookupService.getLanguages();
			this.countryRegionsData =
				await PartnerLookupService.getCountryRegions(this.countryData);
			this.gamingLimitsData =
				await PartnerLookupService.getGamingLimits();
		} catch (error) {
			console.error(error);

			this.isRegistrationSuccessful = false;
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	async register(formData: RegistrationFormData, shopId: string) {
		try {
			this.loader.suspend();

			const body = this.prepareRegistrationFormData(
				formData,
				null,
				"aircash"
			);

			const response =
				await PlatformPartnerRegistrationApiService.registerPartner(
					body,
					shopId
				);

			if (
				response?.errorCode ===
				LugasErrorCodes.LugasLimitTakeoverNoExistingLimit
			) {
				this.takeoverTempInfoId = response.tempInfoId;
				return;
			}

			this.isRegistrationSuccessful = true;
			analyticsTracker.trackRegistrationFormCompleted();
		} catch (error) {
			console.error(error);

			registrationValidationSingle(error?.data);
		} finally {
			this.loader.resume();
			document.querySelector("main")?.scrollTo(0, 0);
		}
	}

	@action.bound
	async completeTakeoverWithLimit(limitAmount?: string | null) {
		if (!this.takeoverTempInfoId || !limitAmount) {
			return;
		}

		try {
			this.loader.suspend();

			await PlatformRegistrationApiService.completeTakeoverWithLimit({
				tempInfoId: this.takeoverTempInfoId,
				limitAmount: limitAmount,
				partnerIdentificator: "aircash",
			});

			this.takeoverTempInfoId = null;
			this.isRegistrationSuccessful = true;
			analyticsTracker.trackRegistrationFormCompleted();
		} catch (error) {
			console.error(error);

			registrationValidationSingle(error?.data);
		} finally {
			this.loader.resume();
			document.querySelector("main")?.scrollTo(0, 0);
		}
	}

	@action.bound
	async checkUserNameAvailability(username: string) {
		try {
			const response =
				await PlatformPartnerRegistrationApiService.checkUserNameAvailability(
					{
						validationProperty: username,
					}
				);
			return validateBooleanResponse(
				response,
				"partnerRegistrationStore-checkUserNameAvailability"
			);
		} catch (error) {
			console.error(error);
			return false;
		}
	}

	@action.bound
	async checkIsEmailAvailable(email: string) {
		try {
			const response =
				await PlatformPartnerRegistrationApiService.checkEmailAvailability(
					{
						validationProperty: email,
					}
				);
			return validateBooleanResponse(
				response,
				"partnerRegistrationStore-checkIsEmailAvailable"
			);
		} catch (error) {
			console.error(error);
			return false;
		}
	}

	@action.bound
	async checkPasswordSimilarity(
		dataset: PasswordSimilarityRequestModel
	): Promise<boolean> {
		let response = false;

		const { password, ...rest } = dataset;
		const valuesToCompareTo = [];

		for (const [_, value] of Object.entries(rest)) {
			if (value == null || value === "") {
				continue;
			}

			valuesToCompareTo.push(value);
		}

		try {
			const result =
				await PlatformPartnerRegistrationApiService.checkPasswordSimilarity(
					{
						validationProperty: password,
						compareWithValues: valuesToCompareTo,
					}
				);

			response = validatePasswordSimilarityResponse(
				result,
				"partnerRegistrationStore-checkPasswordSimilarity"
			);
		} catch (error) {
			console.error(error);
		}

		return response;
	}

	@action.bound
	onDispose() {
		this.currentStepReactionDisposer?.();
		this.currentStep = 1;
		this.isRegistrationSuccessful = null;
		this.takeoverTempInfoId = null;

		//lookups
		this.titleData = [];
		this.countryData = [];
		this.languageData = [];
		this.countryRegionsData = [];
		this.gamingLimitsData = null;
	}
}
